/**
 * Stale-client detector. The running bundle knows its own content hash (from
 * its module URL); the server reports the hash it is currently serving at
 * /api/version. When they diverge a deploy happened, and mobile browsers
 * cache index.html hard enough that users would otherwise stay on the old
 * version indefinitely, so the client announces and force-reloads itself.
 */

const CHECK_INTERVAL_MS: number = 5 * 60_000;
const RELOAD_DELAY_MS: number = 5_000;

/** Extract this bundle's content hash from its own module URL
 *  (…/assets/index-XXXX.js). Empty string in dev (unbundled). */
export function runningVersion(moduleUrl: string): string {
  const match: RegExpMatchArray | null = moduleUrl.match(/assets\/index-([\w-]+)\.js/);
  return match === null ? "" : match[1];
}

/** True when a reload is warranted: both sides know their version and they
 *  disagree. Dev (empty local hash) and server "dev" never trigger. */
export function isStale(local: string, served: string): boolean {
  return local !== "" && served !== "" && served !== "dev" && local !== served;
}

export function startVersionWatch(serverHttpUrl: string, onStale: (reload: () => void) => void): void {
  const local: string = runningVersion(import.meta.url);
  if (local === "") {
    return; // dev server: nothing to compare
  }
  let announced: boolean = false;
  const check = async (): Promise<void> => {
    try {
      const res: Response = await fetch(`${serverHttpUrl}/api/version`, { cache: "no-store" });
      if (!res.ok) {
        return;
      }
      const data = (await res.json()) as { version: string };
      if (!announced && isStale(local, data.version)) {
        announced = true;
        onStale(() => {
          setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
        });
      }
    } catch {
      // Offline or server restarting: try again next tick.
    }
  };
  setInterval(() => {
    void check();
  }, CHECK_INTERVAL_MS);
  // Also check when the app returns to the foreground — the typical moment
  // a phone resurrects a days-old cached session.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void check();
    }
  });
}
